import { API_ENDPOINTS } from 'constants/apiEndpoints';
import httpClient from 'services/httpClient';

/**
 * Auth service — the ONLY place auth API calls live.
 * Components/hooks call these methods; they never touch httpClient directly.
 */
export const authService = {
  login: (credentials) => httpClient.post(API_ENDPOINTS.AUTH.LOGIN, credentials),
  // Second factor for a login that returned `mfa_required` (email-OTP, not MFA setup).
  mfaVerifyLogin: (payload) => httpClient.post(API_ENDPOINTS.AUTH.MFA_VERIFY, payload),
  register: (payload) => httpClient.post(API_ENDPOINTS.AUTH.REGISTER, payload),
  logout: () => httpClient.post(API_ENDPOINTS.AUTH.LOGOUT),
  // Switch the active panel for a user who holds multiple roles in the same
  // center (e.g. mentor + jury). Returns a NEW access token with the new
  // active_assignment. { data: { token, active_assignment, assignments, ... } }.
  switchAssignment: (assignment_type) =>
    httpClient.post(API_ENDPOINTS.AUTH.SWITCH_ASSIGNMENT, { assignment_type }),
  // Super-admin "Switch Center": list the centers the caller may switch between
  // ({ data: { current_center_id, organization_id, centers: [{ id, name, location }] } }).
  getSwitchCenters: () => httpClient.get(API_ENDPOINTS.AUTH.SWITCH_CENTERS),
  // The center/organization this session belongs to, for the sidebar tenant
  // badge ({ data: { scope, id, name, logo_url } }; nulls for the master).
  getActiveTenant: () => httpClient.get(API_ENDPOINTS.AUTH.TENANT),
  // Switch the active context and get a NEW scoped access token. Pass
  // `{ center_id }` to enter a specific center, or `{ organization_id }` to
  // return to organization scope (all assigned centers) — one is required.
  switchCenter: (payload) =>
    httpClient.post(API_ENDPOINTS.AUTH.SWITCH_CONTEXT, payload),
  getProfile: () => httpClient.get(API_ENDPOINTS.AUTH.ME),
  // Self-service profile + password (My Profile / Settings pages).
  // Accepts a plain object (JSON) or FormData (multipart, for avatar upload).
  updateProfile: (payload) => {
    const isForm = typeof FormData !== 'undefined' && payload instanceof FormData;
    return httpClient.put(
      API_ENDPOINTS.AUTH.ME,
      payload,
      isForm ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    );
  },
  changePassword: (payload) => httpClient.post('/users/change-password', payload),
  // First-login flow — clears users.must_change_password and returns a refreshed token.
  setInitialPassword: (payload) => httpClient.post(API_ENDPOINTS.AUTH.SET_INITIAL_PASSWORD, payload),

  // Reference-number prefix settings (center admin — Settings page).
  getPrefixSettings: () => httpClient.get('/users/prefix-settings'),
  updatePrefixSettings: (payload) => httpClient.put('/users/prefix-settings', payload),

  // Multi-factor auth (email-OTP) — Settings › Security.
  mfaInitiate: () => httpClient.post('/auth/mfa/setup/initiate'),
  mfaVerify: (otp) => httpClient.post('/auth/mfa/setup/verify', { otp }),
  mfaDisable: () => httpClient.post('/auth/mfa/disable'),
  mfaRegenerateBackupCodes: () => httpClient.post('/auth/mfa/backup/regenerate'),

  forgotPassword: (payload) => httpClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, payload),
  verifyOtp: (payload) => httpClient.post(API_ENDPOINTS.AUTH.VERIFY_OTP, payload),
  resetPassword: (payload) => httpClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, payload),
  // RBAC matrices used to gate pages/actions.
  getPermissions: () => httpClient.get(API_ENDPOINTS.AUTH.PERMISSIONS),
  getResourcePermissions: () => httpClient.get(API_ENDPOINTS.AUTH.ME_PERMISSIONS),
};

export default authService;
